import { RefreshCw, XCircle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { calculatePnl } from "@/utils/pnl";

export const RolloverModal = ({
  isOpen,
  onClose,
  trade,
  rolloverForm,
  setRolloverForm,
  onRollover,
  isRollingOver,
  nextExpiryInfo,
  loadingNextExpiry,
}) => {
  if (!trade) return null;

  // Calculate rollover summary using correct multiplier
  const getRolloverSummary = () => {
    if (!rolloverForm.exit_price || !rolloverForm.new_entry_price) return null;
    
    const exitPrice = parseFloat(rolloverForm.exit_price);
    const newEntryPrice = parseFloat(rolloverForm.new_entry_price);
    const newNumLots = parseInt(rolloverForm.new_num_lots) || trade.num_lots;
    const newLotSize = parseInt(rolloverForm.new_lot_size) || trade.lot_size;
    
    const { pnlAmount } = calculatePnl({
      direction: trade.direction,
      entryPrice: trade.entry_price,
      exitPrice,
      numLots: trade.num_lots || 1,
      trade,
    });
    const rolloverSpread = newEntryPrice - exitPrice;
    
    return { pnlAmount, rolloverSpread, newNumLots, newLotSize };
  };

  const summary = getRolloverSummary();

  const handleClose = (open) => {
    onClose(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-charcoal border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-400" />
            Rollover Trade - {trade.symbol}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Close current contract and open new position in next expiry period
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Current Trade Summary */}
          <div className="bg-black/20 p-3 rounded-md text-sm">
            <p className="text-slate-500 mb-2 font-medium">Current Position:</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-slate-500">Direction:</span>{" "}
                <span className={trade.direction === "LONG" ? "text-profit" : "text-loss"}>
                  {trade.direction}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Entry Price:</span>{" "}
                ₹{trade.entry_price?.toLocaleString('en-IN')}
              </div>
              <div>
                <span className="text-slate-500">Quantity:</span>{" "}
                {trade.num_lots || 0} × {trade.lot_size || 1}
              </div>
            </div>
          </div>

          {/* Close Current Contract */}
          <div className="border border-loss/30 rounded-md p-4">
            <h4 className="text-loss font-medium mb-3 flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Step 1: Close Current Contract
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-sm">Exit Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rolloverForm.exit_price}
                  onChange={(e) => setRolloverForm(prev => ({ ...prev, exit_price: e.target.value }))}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  data-testid="rollover-exit-price"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Exit Date</Label>
                <Input
                  type="datetime-local"
                  value={rolloverForm.exit_date}
                  onChange={(e) => setRolloverForm(prev => ({ ...prev, exit_date: e.target.value }))}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  data-testid="rollover-exit-date"
                />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-slate-300 text-sm">Exit Comments</Label>
              <Textarea
                value={rolloverForm.exit_comments}
                onChange={(e) => setRolloverForm(prev => ({ ...prev, exit_comments: e.target.value }))}
                className="bg-black/20 border-white/10 text-white mt-1"
                placeholder="Reason for closing current contract (e.g., Contract expiry, rollover to next month)..."
                rows={2}
                data-testid="rollover-exit-comments"
              />
            </div>
          </div>

          {/* Open New Contract */}
          <div className="border border-profit/30 rounded-md p-4">
            <h4 className="text-profit font-medium mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Step 2: Open New Contract (Next Expiry)
            </h4>
            
            {/* Next Expiry Info Banner */}
            {loadingNextExpiry && (
              <div className="bg-blue-900/20 border border-blue-500/30 p-2 rounded-md mb-3 text-sm">
                <span className="text-blue-400">Fetching next expiry contract price...</span>
              </div>
            )}
            {nextExpiryInfo && (
              <div className="bg-blue-900/20 border border-blue-500/30 p-2 rounded-md mb-3 text-sm">
                <span className="text-slate-400">Next Contract: </span>
                <span className="text-blue-400 font-mono">{nextExpiryInfo.next_contract_symbol}</span>
                <span className="text-slate-500 ml-2">(Expiry: {nextExpiryInfo.next_expiry})</span>
                {nextExpiryInfo.ltp && (
                  <span className="ml-2 text-profit">CMP: ₹{nextExpiryInfo.ltp.toLocaleString('en-IN')}</span>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-sm">New Entry Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rolloverForm.new_entry_price}
                  onChange={(e) => setRolloverForm(prev => ({ ...prev, new_entry_price: e.target.value }))}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  placeholder={loadingNextExpiry ? "Loading..." : "Enter price"}
                  data-testid="rollover-new-entry-price"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">New Entry Date</Label>
                <Input
                  type="datetime-local"
                  value={rolloverForm.new_entry_date}
                  onChange={(e) => setRolloverForm(prev => ({ ...prev, new_entry_date: e.target.value }))}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  data-testid="rollover-new-entry-date"
                />
              </div>
            </div>
            
            {/* Contract Expiry and Quantity Fields */}
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <Label className="text-slate-300 text-sm">Contract Expiry</Label>
                <Input
                  type="text"
                  value={rolloverForm.new_contract_expiry}
                  onChange={(e) => setRolloverForm(prev => ({ ...prev, new_contract_expiry: e.target.value }))}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  placeholder={nextExpiryInfo?.next_expiry || "e.g., 27FEB2025"}
                  data-testid="rollover-contract-expiry"
                />
                <p className="text-xs text-slate-500 mt-1">Format: DDMMMYYYY</p>
              </div>
              <div>
                <Label className="text-slate-300 text-sm"># of Lots</Label>
                <Input
                  type="number"
                  min="1"
                  value={rolloverForm.new_num_lots}
                  onChange={(e) => setRolloverForm(prev => ({ ...prev, new_num_lots: e.target.value }))}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  placeholder={trade.num_lots || ""}
                  data-testid="rollover-num-lots"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Lot Size</Label>
                <Input
                  type="number"
                  min="1"
                  value={rolloverForm.new_lot_size}
                  onChange={(e) => setRolloverForm(prev => ({ ...prev, new_lot_size: e.target.value }))}
                  className="bg-black/20 border-white/10 text-white mt-1"
                  placeholder={trade.lot_size || ""}
                  data-testid="rollover-lot-size"
                />
              </div>
            </div>
            
            <div className="mt-3">
              <Label className="text-slate-300 text-sm">Entry Comments</Label>
              <Textarea
                value={rolloverForm.new_entry_comments}
                onChange={(e) => setRolloverForm(prev => ({ ...prev, new_entry_comments: e.target.value }))}
                className="bg-black/20 border-white/10 text-white mt-1"
                placeholder="Notes for new position (e.g., Rollover from previous contract, continuation of trend)..."
                rows={2}
                data-testid="rollover-entry-comments"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              New trade will be created with {trade.direction} direction. Adjust lots and lot size if needed.
            </p>
          </div>

          {/* Rollover Summary */}
          {summary && (
            <div className="bg-blue-900/20 border border-blue-500/30 p-3 rounded-md">
              <p className="text-blue-400 text-sm font-medium mb-2">Rollover Summary:</p>
              <div className="text-sm space-y-1">
                <p>
                  <span className="text-slate-400">P&L from closing:</span>{" "}
                  <span className={summary.pnlAmount >= 0 ? 'text-profit' : 'text-loss'}>
                    ₹{summary.pnlAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </p>
                <p>
                  <span className="text-slate-400">Rollover spread:</span>{" "}
                  <span className={summary.rolloverSpread >= 0 ? 'text-yellow-400' : 'text-profit'}>
                    ₹{summary.rolloverSpread.toFixed(2)}
                  </span>
                  {" "}per unit ({summary.rolloverSpread >= 0 ? 'contango' : 'backwardation'})
                </p>
                <p>
                  <span className="text-slate-400">New position:</span>{" "}
                  <span className="text-white">{summary.newNumLots} × {summary.newLotSize}</span>
                  {rolloverForm.new_contract_expiry && (
                    <span className="text-slate-500"> (Expiry: {rolloverForm.new_contract_expiry})</span>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={() => handleClose(false)} 
            className="border-white/10 text-slate-300"
          >
            Cancel
          </Button>
          <Button 
            onClick={onRollover} 
            disabled={isRollingOver || loadingNextExpiry} 
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isRollingOver ? "Processing..." : "Execute Rollover"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RolloverModal;
